// El editor de un post, AL LADO del calendario —
// ADR-2026-09-15-el-post-se-edita-al-lado-del-calendario.
//
// Era un sheet lateral que tapaba la pantalla. Ahora es el mismo gesto que la
// ficha de un producto: el calendario se achica a una columna angosta y el
// editor se abre al lado, con las MISMAS cards (`Seccion`) que la ficha. Un
// lanzamiento no puede tener dos maneras de editar lo que lleva adentro.

import { useEffect, useState } from 'react';
import {
  AlertCircle,
  AlignLeft,
  ArrowLeft,
  CalendarClock,
  Check,
  ClipboardCheck,
  ClipboardCopy,
  Download,
  Images,
  Loader2,
  Play,
  Sparkles,
  Trash2,
  Wand2,
} from 'lucide-react';
import SortableImageList from '../../../components/tnexport/SortableImageList';
import { descargarSueltos } from '../../../utils/descargarLote';
import VisorDeFoto from '../VisorDeFoto';
import { AgregarFoto } from '../AgregarFoto';
import { Seccion } from '../Seccion';
import { SelectorDeCreaciones } from '../SelectorDeCreaciones';
import {
  firmarFull,
  quitarImagen,
  reordenarImagenes,
  subirImagenAProducto,
  sumarGeneradasAProducto,
} from '../lanzamientosApi';
import { escribirCopy } from './copyIa';
import { nombresDeLasPiezas } from './descargarPost';
import { comoSeLee, sugerirMomento, type PostEnAgenda } from './mejorMomento';
import {
  MAX_PIEZAS,
  MAX_TEXTO,
  avisoDeSeccion,
  contarHashtags,
  estaVacio,
  formatoDe,
  nombreDelFormato,
  problemasConSeccion,
} from './post';
import { borrarPost, guardarPost, type PostConMiniaturas } from './postsApi';

/** `<input type="date">` quiere `2026-09-15`; `type="time"`, `18:00`. En hora LOCAL. */
function partesDeFecha(d: Date | null): { dia: string; hora: string } {
  if (!d) return { dia: '', hora: '' };
  const p = (n: number) => String(n).padStart(2, '0');
  return {
    dia: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`,
    hora: `${p(d.getHours())}:${p(d.getMinutes())}`,
  };
}

/** Los dos inputs de vuelta a una fecha. `null` si falta alguno. */
function aFecha(dia: string, hora: string): Date | null {
  if (!dia || !hora) return null;
  const [a, m, d] = dia.split('-').map(Number);
  const [hh, mm] = hora.split(':').map(Number);
  if (!a || !m || !d) return null;
  // Por componentes y NO `new Date("2026-09-15T18:00")`: esa forma la interpretan
  // distinto los navegadores y el post se movía de hora solo.
  return new Date(a, m - 1, d, hh ?? 0, mm ?? 0);
}

export function EditorDePost({
  post,
  companyId,
  agenda,
  desde,
  aceptaVideo,
  onCambio,
  onCerrar,
}: {
  post: PostConMiniaturas;
  companyId: string;
  /** Todo lo planificado de la empresa, para no pisarse. */
  agenda: PostEnAgenda[];
  /** No se agenda antes de acá. */
  desde: Date | null;
  aceptaVideo: boolean;
  onCambio: () => void;
  onCerrar: () => void;
}) {
  const [texto, setTexto] = useState(post.texto);
  const [{ dia, hora }, setCuando] = useState(partesDeFecha(post.cuando));
  const [porque, setPorque] = useState<string | null>(null);
  const [escribiendo, setEscribiendo] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [eligiendo, setEligiendo] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  /** Qué pieza se está mirando ampliada, y su URL completa ya firmada. */
  const [viendo, setViendo] = useState<string | null>(null);
  const [urlAmpliada, setUrlAmpliada] = useState<string | null>(null);
  const [bajando, setBajando] = useState(false);
  const [copiado, setCopiado] = useState(false);

  // Se abrió OTRO post: los campos arrancan de cero.
  useEffect(() => {
    setTexto(post.texto);
    setCuando(partesDeFecha(post.cuando));
    setPorque(null);
    setAviso(null);
  }, [post.id]);

  // La URL completa se firma recién al abrir el visor: firmar las diez de
  // entrada sería diez llamadas para una que se va a mirar.
  useEffect(() => {
    if (!viendo) return setUrlAmpliada(null);
    const m = post.medios.find((x) => x.id === viendo);
    if (!m) return;
    let vivo = true;
    void firmarFull(companyId, m.fileName).then((u) => {
      if (vivo) setUrlAmpliada(u);
    });
    return () => {
      vivo = false;
    };
  }, [viendo, companyId, post.medios]);

  const fecha = aFecha(dia, hora);
  const formato = formatoDe(post.medios);
  const problemas = problemasConSeccion({ ...post, texto, cuando: fecha });
  const tags = contarHashtags(texto);

  const guardar = async (campos: { texto?: string; cuando?: Date | null }) => {
    const r = await guardarPost(post.id, campos);
    if (r.error) setAviso(r.error);
    else onCambio();
  };

  /**
   * La fecha se guarda SÓLO cuando está completa.
   *
   * Los dos inputs son uno solo para la base: vaciar el día para retipearlo
   * dejaba `aFecha` en null y eso mandaba `programado_at = null`, que el check
   * `lp_producto_o_post` de la migración rechaza —un post no tiene
   * `producto_id`, así que la fila quedaría sin ninguna de las dos cosas—. El
   * update volvía con error, la pantalla mostraba la fecha vacía y la fila
   * seguía con la anterior: lo que se veía y lo guardado dejaban de coincidir.
   *
   * Mientras está a medio escribir se queda local. La card ya avisa en ámbar
   * "Falta la fecha y la hora" porque los problemas se calculan sobre el estado
   * de la pantalla, no sobre la fila
   * (ADR-2026-09-16-la-fecha-a-medio-escribir-no-viaja-a-la-base).
   */
  const guardarCuando = (f: Date | null) => {
    setPorque(null);
    if (f) void guardar({ cuando: f });
  };

  const sugerir = () => {
    const s = sugerirMomento({ agenda, ahora: new Date(), desde, excluirId: post.id });
    setCuando(partesDeFecha(s.cuando));
    setPorque(s.porque);
    void guardar({ cuando: s.cuando });
  };

  const conIa = async () => {
    setEscribiendo(true);
    setAviso(null);
    const r = await escribirCopy(post.medios.map((m) => m.fileName).filter(Boolean));
    setEscribiendo(false);
    if (r.error || !r.texto) return setAviso(r.error);
    setTexto(r.texto);
    void guardar({ texto: r.texto });
  };

  const subir = async (f: File) => {
    setSubiendo(true);
    setAviso(null);
    const r = await subirImagenAProducto(post.id, companyId, f, null, aceptaVideo);
    setSubiendo(false);
    if (r.error) setAviso(r.error);
    else onCambio();
  };

  /**
   * Volver al calendario. Si el post quedó sin nada, se descarta: la fila se crea
   * al tocar "+" para poder colgarle las fotos, así que asomarse y volver dejaba
   * un fantasma en el mes (ADR-2026-09-15-un-post-al-que-no-se-le-puso-nada-se-descarta).
   *
   * Va en el cierre explícito y NO en un efecto de desmontaje: la app corre en
   * StrictMode, que monta, desmonta y vuelve a montar en desarrollo — un
   * `cleanup` borraría el post recién creado antes de que nadie lo toque.
   */
  const cerrar = () => {
    // `subiendo`/`escribiendo` mandan sobre el vacío: mientras una subida o el
    // copy de la IA están EN VUELO el post todavía no tiene la pieza ni el
    // texto, así que `estaVacio` dice que sí y lo borraba en medio de la
    // escritura. La subida seguía igual y terminaba colgándole el medio a una
    // fila muerta, o se borraba en cascada con ella. Un post que está por
    // recibir algo no está vacío: se cierra y se deja quieto
    // (ADR-2026-09-16-un-post-con-algo-en-vuelo-no-esta-vacio).
    const enVuelo = subiendo || escribiendo;
    if (!enVuelo && estaVacio({ ...post, texto })) {
      void borrarPost(post.id).then(onCambio);
    }
    onCerrar();
  };

  /**
   * Bajar las piezas del post, SUELTAS y nunca en un ZIP (pedido de Mati): de un
   * post se sacan las piezas para subirlas a otro lado, y descomprimir en el
   * medio es un paso que no aporta nada.
   *
   * `descargarSueltos` las manda de a una con una pausa entre medio —sin eso el
   * navegador corta la ráfaga— y ya resuelve corregir la extensión por los BYTES
   * (hay imágenes guardadas como `.png` que por dentro son JPEG) y saltear la
   * que falle sin cortar el resto.
   */
  const descargar = async () => {
    if (post.medios.length === 0 || bajando) return;
    setBajando(true);
    setAviso(null);
    const nombres = nombresDeLasPiezas(post.medios, fecha);
    const urls = await Promise.all(post.medios.map((m) => firmarFull(companyId, m.fileName)));
    const archivos = post.medios
      // `imagenId`: las piezas que son creaciones quedan marcadas como descargadas.
      .map((m, i) => ({ url: urls[i] ?? '', nombre: nombres[i] ?? 'post', imagenId: m.generadaId ?? undefined }))
      .filter((a) => a.url);
    // Las que no se pudieron firmar no llegan a `descargarSueltos`, así que no
    // salen en su conteo: sin sumarlas acá, una pieza que falla al firmar y dos
    // que bajan bien daban "2 ok, 0 fallaron" y el usuario se llevaba un
    // carrusel incompleto sin enterarse.
    const sinFirmar = post.medios.length - archivos.length;
    const r = await descargarSueltos(archivos);
    const fallidos = r.fallidos + sinFirmar;
    setBajando(false);
    if (r.ok === 0) setAviso('No se pudo descargar');
    else if (fallidos > 0) setAviso(`Se bajaron ${r.ok}; ${fallidos} fallaron`);
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 1800);
    } catch {
      // Pasa sin HTTPS y con el portapapeles bloqueado por el navegador.
      setAviso('El navegador no dejó copiar. Seleccioná el texto y copialo a mano.');
    }
  };

  const reordenar = async (idsEnOrden: string[]) => {
    const r = await reordenarImagenes(idsEnOrden);
    if (r.error) setAviso(r.error);
    else onCambio();
  };

  return (
    <div>
      {/* Misma salida que la ficha de un producto: el volver arriba de todo. */}
      <button
        type="button"
        onClick={cerrar}
        className="mb-3 flex items-center gap-1.5 text-sm text-neutral-500 hover:text-brand-black"
      >
        <ArrowLeft className="w-4 h-4" /> Todo el calendario
      </button>

      <div className="space-y-3">
        {/* Encabezado: lo único con peso visual es CUÁNDO sale, que es el dato
            que define a un post del calendario. */}
        <div className="rounded-2xl border border-neutral-200 bg-surface-card p-4 shadow-sm sm:p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="truncate text-base font-semibold text-brand-black">
                {nombreDelFormato(formato)}
              </h2>
              <p className="mt-0.5 text-xs text-neutral-500">
                {fecha ? `Sale el ${comoSeLee(fecha)}` : 'Todavía sin fecha'}
                {post.medios.length > 0 &&
                  ` · ${post.medios.length} pieza${post.medios.length === 1 ? '' : 's'}`}
              </p>
            </div>
            {/* Todo lo de la derecha en UN renglón: las dos acciones del post y,
                cerrando, el estado. Antes las acciones iban abajo y a la
                izquierda; así el encabezado tiene el nombre a un lado y todo lo
                accionable al otro, alineado a la derecha
                (ADR-2026-09-16-las-acciones-del-post-comparten-renglon-con-el-estado). */}
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => void descargar()}
                disabled={post.medios.length === 0 || bajando}
                className="flex items-center gap-1.5 rounded-xl border border-neutral-200 py-2 px-4 text-xs font-medium text-brand-black transition-colors hover:bg-neutral-50 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                {bajando ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-neutral-400" />
                ) : (
                  <Download className="w-3.5 h-3.5 text-neutral-400" />
                )}
                {bajando ? 'Bajando…' : 'Descargar'}
              </button>
              <button
                type="button"
                onClick={() => void copiar()}
                disabled={!texto.trim()}
                className="flex items-center gap-1.5 rounded-xl border border-neutral-200 py-2 px-4 text-xs font-medium text-brand-black transition-colors hover:bg-neutral-50 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                {copiado ? (
                  <ClipboardCheck className="w-3.5 h-3.5 text-brand-primary" />
                ) : (
                  <ClipboardCopy className="w-3.5 h-3.5 text-neutral-400" />
                )}
                {copiado ? 'Copiada' : 'Copiar descripción'}
              </button>

              {/* La píldora cierra el renglón, como en la ficha de un producto:
                  es el estado, no una acción, y va en la esquina. */}
              {post.estado === 'publicado' ? (
              <span className="shrink-0 rounded-full bg-neutral-100 py-1 px-2.5 text-xs font-medium text-neutral-600">
                Publicado
              </span>
            ) : (
              <span
                className={`flex shrink-0 items-center gap-1.5 rounded-full py-1 px-2.5 text-xs font-medium ${
                  problemas.length === 0 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-800'
                }`}
              >
                {problemas.length === 0 ? (
                  <>
                    <Check className="w-3.5 h-3.5" /> Listo
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-3.5 h-3.5" /> Falta{' '}
                    {problemas.length > 1 ? `${problemas.length} datos` : '1 dato'}
                  </>
                )}
              </span>
              )}
            </div>
          </div>
        </div>

        {aviso && (
          <p className="rounded-xl border border-neutral-200 bg-neutral-50 py-3 px-4 text-sm text-brand-black">
            {aviso}
          </p>
        )}

        {/* ── Las piezas ─────────────────────────────────────────────────── */}
        <Seccion
          titulo="Fotos y videos"
          icono={<Images className="w-4 h-4" />}
          abiertaPorDefecto
          resumen={`${post.medios.length} de ${MAX_PIEZAS}`}
          aviso={avisoDeSeccion(problemas, 'medios')}
        >
          {post.medios.length > 0 && (
            <p className="text-xs text-neutral-500">
              La primera es la portada: es la que frena el scroll.
            </p>
          )}

          {/* La MISMA tira que la ficha de un producto: arrastrar para reordenar,
              la × para quitar y la card punteada para sumar. No hay motivo para
              que las fotos de un post se manejen distinto que las de un producto
              (ADR-2026-09-15-las-fotos-de-un-post-se-arrastran-como-las-de-un-producto).
              El número que dibuja la tira ES el orden del carrusel: la 1 es la portada. */}
          <div className="flex flex-wrap items-start gap-3">
            {post.medios.length > 0 && (
              <SortableImageList
                grande
                imagenes={post.medios.map((m, i) => ({
                  id: m.id,
                  orden: m.orden,
                  thumbUrl: post.miniaturas[i] ?? '',
                }))}
                onReorder={(ids) => void reordenar(ids)}
                onRemove={(id) => void quitarImagen(id).then(onCambio)}
                onVer={setViendo}
                accionExtra={(id) =>
                  post.medios.find((m) => m.id === id)?.esVideo ? (
                    <span
                      aria-label="Video"
                      title="Video"
                      className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-white"
                    >
                      <Play className="w-3 h-3" />
                    </span>
                  ) : null
                }
              />
            )}
            <AgregarFoto
              subiendo={subiendo}
              deshabilitada={post.medios.length >= MAX_PIEZAS}
              aceptaVideo={aceptaVideo}
              tituloDeshabilitada={`Un carrusel admite hasta ${MAX_PIEZAS} piezas`}
              onElegirGenerada={() => setEligiendo(true)}
              onSubir={(f) => void subir(f)}
            />
          </div>
        </Seccion>

        {/* ── El copy ────────────────────────────────────────────────────── */}
        {/* Sin plegar: son los dos únicos campos del post y plegarlos escondía
            justo lo que se viene a hacer. La de medios sí se pliega, que puede
            tener diez piezas. */}
        <Seccion
          titulo="Descripción"
          icono={<AlignLeft className="w-4 h-4" />}
          plegable={false}
          aviso={avisoDeSeccion(problemas, 'texto')}
        >
          <textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onBlur={() => void guardar({ texto })}
            rows={8}
            placeholder="Qué querés contar con este post…"
            className="w-full resize-y rounded-xl border border-neutral-200 py-2.5 px-3 text-sm text-brand-black outline-none placeholder:text-neutral-400 focus:border-brand-primary"
          />
          {/* La ayuda va DESPUÉS del campo, no antes: primero se ve qué hay que
              completar y recién después la salida de emergencia. Mismo lugar
              relativo que en "Cuándo sale"
              (ADR-2026-09-15-la-ayuda-va-despues-del-campo). */}
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => void conIa()}
              disabled={escribiendo || post.medios.length === 0}
              className="flex items-center gap-1.5 text-sm text-brand-primary disabled:text-neutral-300"
            >
              {escribiendo ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5" />
              )}
              {escribiendo ? 'Escribiendo…' : 'Escribir con IA'}
            </button>
            <span className="shrink-0 text-xs text-neutral-400">
              {tags} hashtag{tags === 1 ? '' : 's'} ·{' '}
              <span className={texto.length > MAX_TEXTO ? 'text-brand-black' : undefined}>
                {texto.length}/{MAX_TEXTO}
              </span>
            </span>
          </div>
        </Seccion>

        {/* ── Cuándo sale ────────────────────────────────────────────────── */}
        <Seccion
          titulo="Cuándo sale"
          icono={<CalendarClock className="w-4 h-4" />}
          plegable={false}
          aviso={avisoDeSeccion(problemas, 'cuando')}
        >
          <div className="flex gap-2">
            <input
              type="date"
              value={dia}
              onChange={(e) => {
                const v = e.target.value;
                setCuando((p) => ({ ...p, dia: v }));
                guardarCuando(aFecha(v, hora));
              }}
              className="flex-1 rounded-xl border border-neutral-200 py-2.5 px-3 text-sm text-brand-black outline-none focus:border-brand-primary"
            />
            <input
              type="time"
              value={hora}
              onChange={(e) => {
                const v = e.target.value;
                setCuando((p) => ({ ...p, hora: v }));
                guardarCuando(aFecha(dia, v));
              }}
              className="w-32 rounded-xl border border-neutral-200 py-2.5 px-3 text-sm text-brand-black outline-none focus:border-brand-primary"
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={sugerir}
              className="flex items-center gap-1.5 text-sm text-brand-primary"
            >
              <Wand2 className="w-3.5 h-3.5" /> Sugerirme un momento
            </button>
            {porque && (
              <span className="min-w-0 truncate text-xs text-neutral-400" title={porque}>
                {porque}
              </span>
            )}
          </div>
        </Seccion>

        <button
          type="button"
          onClick={() =>
            void borrarPost(post.id).then(() => {
              onCambio();
              onCerrar();
            })
          }
          className="flex items-center gap-1.5 px-1 text-sm text-neutral-500 hover:text-brand-black"
        >
          <Trash2 className="w-4 h-4" /> Borrar este post
        </button>
      </div>

      {viendo && (
        <VisorDeFoto
          url={urlAmpliada}
          esVideo={!!post.medios.find((m) => m.id === viendo)?.esVideo}
          onCerrar={() => setViendo(null)}
        />
      )}

      <SelectorDeCreaciones
        abierta={eligiendo}
        companyId={companyId}
        // Por la CREACIÓN y no por el vínculo: `m.id` es la fila de
        // `lanzamientos_imagenes`, y el selector tacha comparando contra
        // `imagenes_generadas.id`. Las subidas a mano no tienen ninguna.
        yaUsadas={post.medios.map((m) => m.generadaId).filter((x): x is string => !!x)}
        conVideo={aceptaVideo}
        cupo={Math.max(0, MAX_PIEZAS - post.medios.length)}
        onCerrar={() => setEligiendo(false)}
        onConfirmar={async (elegidas) => {
          const r = await sumarGeneradasAProducto(
            post.id,
            companyId,
            elegidas.map((e) => ({ id: e.id, varianteId: e.varianteId })),
          );
          if (r.error) setAviso(r.error);
          setEligiendo(false);
          onCambio();
        }}
      />
    </div>
  );
}
